import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, BookOpen, Plus, Trash2, Search, Filter } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function QuestionBank() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGrade, setFilterGrade] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [settings, setSettings] = useState(null);

  const [newQuestion, setNewQuestion] = useState({
    question_text: '',
    question_type: 'multiple_choice',
    options: ['', '', '', ''],
    correct_answer: '',
    grade: '',
    subject: '',
    topic: '',
    marks: 2,
    cognitive_level: 'middle'
  });

  useEffect(() => {
    fetchData();
  }, [filterGrade, filterSubject]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (filterGrade) params.append('grade', filterGrade);
      if (filterSubject) params.append('subject', filterSubject);

      const [questionsRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/assessments/question-bank?${params.toString()}`),
        axios.get(`${API_URL}/api/teacher/settings`)
      ]);
      setQuestions(questionsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    
    if (!newQuestion.question_text.trim()) {
      toast.error('Please enter a question');
      return;
    }

    try {
      const questionData = {
        ...newQuestion,
        options: newQuestion.question_type === 'multiple_choice' 
          ? newQuestion.options.filter(o => o.trim()) 
          : []
      };
      
      await axios.post(`${API_URL}/api/assessments/question-bank`, questionData);
      toast.success('Question added to bank');
      setShowAddForm(false);
      setNewQuestion({
        question_text: '',
        question_type: 'multiple_choice',
        options: ['', '', '', ''],
        correct_answer: '',
        grade: '',
        subject: '',
        topic: '',
        marks: 2,
        cognitive_level: 'middle'
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to add question');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/assessments/question-bank/${questionId}`);
      toast.success('Question deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete question');
    }
  };

  const filteredQuestions = questions.filter(q => 
    q.question_text?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.topic?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const cognitiveColors = {
    low: 'bg-green-100 text-green-700',
    middle: 'bg-amber-100 text-amber-700',
    high: 'bg-red-100 text-red-700'
  };

  return (
    <div className="min-h-screen bg-sky-50" data-testid="question-bank-page">
      <header className="glass-header py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Button onClick={() => navigate('/assessments')} variant="ghost" data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Assessments
          </Button>
          <Button
            onClick={() => setShowAddForm(true)}
            className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
            data-testid="add-question-button"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Question
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-slate-900">Question Bank</h2>
            <p className="text-slate-600">{questions.length} questions saved</p>
          </div>
        </div>

        {/* Filters */}
        <div className="glass-card p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search questions..."
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <select
              value={filterGrade}
              onChange={(e) => setFilterGrade(e.target.value)}
              className="px-4 py-2 rounded-xl border border-slate-200 bg-white/80"
              data-testid="filter-grade"
            >
              <option value="">All Grades</option>
              {settings?.grades?.map(g => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
            <select
              value={filterSubject}
              onChange={(e) => setFilterSubject(e.target.value)}
              className="px-4 py-2 rounded-xl border border-slate-200 bg-white/80"
              data-testid="filter-subject"
            >
              <option value="">All Subjects</option>
              {settings?.subjects?.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Questions List */}
        {loading ? (
          <div className="glass-card p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          </div>
        ) : filteredQuestions.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No questions yet</h3>
            <p className="text-slate-600 mb-4">Start building your question bank</p>
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Your First Question
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredQuestions.map(question => (
              <div key={question.question_id} className="glass-card p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                        {question.grade || 'No grade'}
                      </span>
                      <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                        {question.subject || 'No subject'}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-sm ${cognitiveColors[question.cognitive_level] || 'bg-slate-100'}`}>
                        {question.cognitive_level || 'middle'} order
                      </span>
                      <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm">
                        {question.marks} marks
                      </span>
                    </div>
                    <p className="text-slate-900 font-medium mb-2">{question.question_text}</p>
                    {question.options?.length > 0 && (
                      <div className="text-sm text-slate-600 space-y-1">
                        {question.options.map((opt, i) => (
                          <div key={i} className={question.correct_answer === String.fromCharCode(65 + i) ? 'text-green-600 font-medium' : ''}>
                            {String.fromCharCode(65 + i)}) {opt}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={() => handleDeleteQuestion(question.question_id)}
                    variant="ghost"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-${question.question_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Question Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="glass-card p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Add Question to Bank</h3>
              
              <form onSubmit={handleAddQuestion} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Question *</label>
                  <textarea
                    value={newQuestion.question_text}
                    onChange={(e) => setNewQuestion(prev => ({ ...prev, question_text: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white/80 focus:outline-none focus:ring-2 focus:ring-teal-500 min-h-[100px]"
                    placeholder="Enter your question..."
                    data-testid="new-question-text"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Type</label>
                    <select
                      value={newQuestion.question_type}
                      onChange={(e) => setNewQuestion(prev => ({ ...prev, question_type: e.target.value }))}
                      className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-white/80"
                    >
                      <option value="multiple_choice">Multiple Choice</option>
                      <option value="true_false">True/False</option>
                      <option value="short_answer">Short Answer</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Marks</label>
                    <Input
                      type="number"
                      value={newQuestion.marks}
                      onChange={(e) => setNewQuestion(prev => ({ ...prev, marks: parseInt(e.target.value) || 1 }))}
                      min="1"
                      max="20"
                    />
                  </div>
                </div>

                {newQuestion.question_type === 'multiple_choice' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Options</label>
                    <div className="space-y-2">
                      {newQuestion.options.map((opt, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="font-medium text-slate-600">{String.fromCharCode(65 + i)})</span>
                          <Input
                            value={opt}
                            onChange={(e) => {
                              const newOpts = [...newQuestion.options];
                              newOpts[i] = e.target.value;
                              setNewQuestion(prev => ({ ...prev, options: newOpts }));
                            }}
                            placeholder={`Option ${String.fromCharCode(65 + i)}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Correct Answer</label>
                  <Input
                    value={newQuestion.correct_answer}
                    onChange={(e) => setNewQuestion(prev => ({ ...prev, correct_answer: e.target.value }))}
                    placeholder={newQuestion.question_type === 'multiple_choice' ? 'e.g., A' : 'Enter correct answer'}
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Grade</label>
                    <select
                      value={newQuestion.grade}
                      onChange={(e) => setNewQuestion(prev => ({ ...prev, grade: e.target.value }))}
                      className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-white/80"
                    >
                      <option value="">Select</option>
                      {settings?.grades?.map(g => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Subject</label>
                    <select
                      value={newQuestion.subject}
                      onChange={(e) => setNewQuestion(prev => ({ ...prev, subject: e.target.value }))}
                      className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-white/80"
                    >
                      <option value="">Select</option>
                      {settings?.subjects?.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Cognitive Level</label>
                    <select
                      value={newQuestion.cognitive_level}
                      onChange={(e) => setNewQuestion(prev => ({ ...prev, cognitive_level: e.target.value }))}
                      className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-white/80"
                    >
                      <option value="low">Low Order</option>
                      <option value="middle">Middle Order</option>
                      <option value="high">High Order</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Topic (Optional)</label>
                  <Input
                    value={newQuestion.topic}
                    onChange={(e) => setNewQuestion(prev => ({ ...prev, topic: e.target.value }))}
                    placeholder="e.g., Fractions"
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <Button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    variant="outline"
                    className="flex-1 rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                    data-testid="save-question-button"
                  >
                    Save Question
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
